import type { FC } from 'react';

import TooltipWrapper from '@wwe-portal/ui/features/club-store/milestone/tooltip';
import { cn } from '@wwe-portal/ui/lib/lib';

import { ContestLogo } from '../component/contest-logo';
import { SuperStarLogo } from '../component/superstar-logo';
import { type OfferCardProps } from '../type';
import { OfferCardTypeEnum } from '@wwe-portal/entities/offer.type';

type StockQuantityProps = Pick<
  OfferCardProps,
  | 'quantity'
  | 'maxQuantity'
  | 'hasContest'
  | 'badgeIcon'
  | 'type'
  | 'showStock'
  | 'badgeText'
>;

const QuantityContent: FC<
  Pick<StockQuantityProps, 'maxQuantity' | 'quantity' | 'type'>
> = ({ maxQuantity, quantity, type }) => {
  if (quantity == null || maxQuantity == null) return null;

  return (
    <div
      className={cn({
        'w-6 min-w-6': type === OfferCardTypeEnum.SINGLE3,
      })}
    >
      {maxQuantity === 1 ? 'One Time Offer' : `${quantity}/${maxQuantity}`}
    </div>
  );
};

const BadgeContent: FC<
  Pick<StockQuantityProps, 'hasContest' | 'badgeIcon' | 'type' | 'badgeText'>
> = ({ badgeIcon, hasContest, type, badgeText }) => {
  const className = cn({
    'w-[30px] h-[30px]': type === OfferCardTypeEnum.SINGLE1,
    'w-5 h-5': type === OfferCardTypeEnum.SINGLE2,
    'w-[18px] h-[18px]': type === OfferCardTypeEnum.SINGLE3,
  });

  if (badgeIcon) {
    return (
      <span className="top-1 w-5">
        <TooltipWrapper
          content={badgeText && <div className="max-w-60">{badgeText}</div>}
        >
          <SuperStarLogo className={className} logoUrl={badgeIcon} />
        </TooltipWrapper>
      </span>
    );
  }
  if (hasContest) {
    return (
      <span className="top-1 w-5">
        <TooltipWrapper
          content={badgeText && <div className="max-w-60">{badgeText}</div>}
        >
          <ContestLogo className={className} />
        </TooltipWrapper>
      </span>
    );
  }
  return null;
};

export const StockQuantity: FC<StockQuantityProps> = ({
  quantity,
  maxQuantity,
  hasContest,
  badgeIcon,
  type,
  showStock,
  badgeText,
}) => {
  return (
    <div
      className={cn(
        'flex min-w-[34px] flex-col gap-0.5 stroke-2 text-xs font-semibold text-white',
        {
          'min-w-[30px]': type === OfferCardTypeEnum.SINGLE1,
          'min-w-5': type === OfferCardTypeEnum.SINGLE2,
          'min-w-[18px]': type === OfferCardTypeEnum.SINGLE3,
        }
      )}
    >
      <div className="min-h-4 min-w-full stroke-2 text-center font-semibold text-white">
        {showStock && (
          <QuantityContent
            maxQuantity={maxQuantity}
            quantity={quantity}
            type={type}
          />
        )}
      </div>
      <BadgeContent
        badgeIcon={badgeIcon}
        badgeText={badgeText}
        hasContest={hasContest}
        type={type}
      />
    </div>
  );
};
